import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import propertyService from '../../services/propertyService';
import ApplyLinkCard from './ApplyLinkCard';
import InviteApplicantCard from './InviteApplicantCard';
import Alert from '../common/Alert';
import Modal from '../common/Modal';
import StatusBadge from '../common/StatusBadge';
import LoadingSpinner from '../common/LoadingSpinner';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { MANDATE_LABELS } from '../../utils/propertyOptions';
import '../../pages/ApplicantPages.css';
import './PropertyPages.css';

const messageFrom = (err, fallback) => err.response?.data?.message || err.message || fallback;

const PropertyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const justCreated = params.get('created') === '1';

  const [property, setProperty] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const load = useCallback(async () => {
    setProperty(await propertyService.get(id));
  }, [id]);

  useEffect(() => {
    load().catch((err) => setError(messageFrom(err, 'This property could not be loaded.')));
  }, [load]);

  const setStatus = async (status) => {
    setBusy(true);
    setNotice(null);
    try {
      await propertyService.update(id, { status });
      await load();
      setNotice({ type: 'success', message: status === 'active' ? 'The property is active again.' : 'The property is inactive. Its application link is switched off until you reactivate it.' });
    } catch (err) {
      setNotice({ type: 'error', message: messageFrom(err, 'The property could not be updated.') });
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await propertyService.remove(id);
      navigate('/properties');
    } catch (err) {
      setConfirmDelete(false);
      setNotice({ type: 'error', message: messageFrom(err, 'The property could not be deleted.') });
    } finally {
      setBusy(false);
    }
  };

  const uploadMandate = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setNotice(null);
    try {
      await propertyService.uploadMandate(id, file);
      await load();
      setNotice({ type: 'success', message: 'Your proof of authority was received. It is stored privately and will be reviewed.' });
    } catch (err) {
      const errors = err.response?.data?.errors;
      setNotice({ type: 'error', message: errors ? Object.values(errors).flat().join(' ') : messageFrom(err, 'The file could not be uploaded.') });
    } finally {
      setBusy(false);
      e.target.value = '';
    }
  };

  const viewMandate = async () => {
    try {
      const blob = await propertyService.mandateDocument(id);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      setNotice({ type: 'error', message: messageFrom(err, 'The document could not be opened.') });
    }
  };

  if (error) return <Alert type="error" message={error} />;
  if (!property) return <LoadingSpinner />;

  const active = property.status === 'active';
  // A verification past its expiry date no longer counts, even before the daily job marks it expired.
  const mandate = !property.mandate_uploaded
    ? 'none'
    : property.mandate_status === 'verified' && !property.mandate_verified
      ? 'expired'
      : property.mandate_status;
  const count = property.applications_count || 0;

  return (
    <div className="ap-page">
      <p><Link to="/properties">&larr; Properties</Link></p>
      <div className="ap-title-row">
        <h1>{property.address}</h1>
        <StatusBadge status={property.status} text={active ? 'Active' : 'Inactive'} />
      </div>

      {justCreated && (
        <Alert type="success" message="Property added, and its application link is ready. Share the link or QR code below, or invite someone directly." />
      )}
      {notice && <Alert type={notice.type} message={notice.message} dismissible onClose={() => setNotice(null)} />}

      <section className="ap-card" aria-labelledby="facts-title">
        <div className="prop-head">
          <h2 id="facts-title">Details</h2>
          <Link to={`/properties/${id}/edit`} className="ap-button ap-button--ghost">Edit</Link>
        </div>
        <dl className="ap-facts">
          <dt>Monthly rent</dt><dd>{formatCurrency(property.monthly_rent_amount)}</dd>
          <dt>Deposit</dt><dd>{formatCurrency(property.deposit_amount)}</dd>
          <dt>Available from</dt><dd>{formatDate(property.occupation_date)}</dd>
          <dt>Type</dt><dd style={{ textTransform: 'capitalize' }}>{property.property_type}</dd>
        </dl>
      </section>

      <ApplyLinkCard
        propertyId={id}
        disabled={!active}
        disabledReason="This property is inactive, so the link does not work until you reactivate it."
        onChange={() => load()}
      />

      {active && <InviteApplicantCard organisationId={property.organisation_id} propertyId={id} onInvited={load} />}

      <section className="ap-card" aria-labelledby="applicants-title">
        <h2 id="applicants-title">Applicants</h2>
        <p>
          {count === 0
            ? 'No one has applied yet.'
            : `${count} ${count === 1 ? 'application' : 'applications'} so far.`}
        </p>
        {count > 0 && (
          <Link
            to={`/property-applicants?org=${property.organisation_id}&property=${id}`}
            className="btn-primary ap-button"
          >
            Compare applicants
          </Link>
        )}
      </section>

      <section className="ap-card" aria-labelledby="mandate-title">
        <h2 id="mandate-title">Proof you can let this property</h2>
        <p className="ap-muted">
          Applicants want to know who is asking for their information. Upload your title deed, lease or mandate. It is
          stored privately and reviewed by us. Applicants only see that it is verified, never the document.
        </p>
        <p>
          <strong>Status:</strong> {MANDATE_LABELS[mandate] || mandate}
          {property.mandate_verified && property.mandate_expires_at && (
            <span className="ap-muted"> (until {formatDate(property.mandate_expires_at)})</span>
          )}
        </p>
        {property.mandate_review_note && (
          <Alert type="warning" message={`Not accepted: ${property.mandate_review_note} Please upload a new document.`} />
        )}
        <div className="prop-actions">
          <label className="ap-button ap-button--ghost prop-file">
            {property.mandate_uploaded ? 'Replace document' : 'Upload document'}
            <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={uploadMandate} disabled={busy} className="sr-only" />
          </label>
          {property.mandate_uploaded && (
            <button type="button" className="ap-button ap-button--ghost" onClick={viewMandate} disabled={busy}>View my document</button>
          )}
        </div>
      </section>

      <section className="ap-card" aria-labelledby="manage-title">
        <h2 id="manage-title">Manage</h2>
        <p className="ap-muted">
          Make a property inactive when it is let. Its link stops working, and existing applications are kept. A property
          can only be deleted if nobody has applied for it.
        </p>
        <div className="prop-actions">
          <button type="button" className="ap-button ap-button--ghost" onClick={() => setStatus(active ? 'inactive' : 'active')} disabled={busy}>
            {active ? 'Make inactive' : 'Make active again'}
          </button>
          <button type="button" className="ap-button ap-button--danger" onClick={() => setConfirmDelete(true)} disabled={busy || count > 0} title={count > 0 ? 'This property has applications' : undefined}>
            Delete property
          </button>
        </div>
      </section>

      <Modal isOpen={confirmDelete} onClose={() => setConfirmDelete(false)} title="Delete this property?">
        <p>This removes <strong>{property.address}</strong> and its application link. This cannot be undone.</p>
        <div className="prop-actions">
          <button type="button" className="ap-button ap-button--danger" onClick={remove} disabled={busy}>Delete</button>
          <button type="button" className="ap-button ap-button--ghost" onClick={() => setConfirmDelete(false)}>Keep it</button>
        </div>
      </Modal>
    </div>
  );
};

export default PropertyDetail;
